import {
  ClinicCopayRuleKind,
  IClinicCopayRule,
  IClinicCopayRuleSaveRequest,
} from "types/ClinicCopayRule/ClinicCopayRule";
import { PracticeTypeRuleModality } from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

/**
 * CLE-3050 — the words and checks of Eligibility › Clinic copay rules, in one place so the table and
 * the dialog say the same thing. The dialog's checks are the API's 400s / 409s, made before anything
 * is sent.
 */

export const CLINIC_COPAY_INTRO =
  "At a clinic, some payers charge an extra copay on top of the office-visit copay when a scan or test is done at the same visit. These rules say which payers do, for which CPT groups. Every clinic uses the same rules.";

export const CLINIC_COPAY_RULES = [
  "One add-on copay per visit: the highest amount among the payer's enabled rules whose CPT group the estimate has.",
  "The amount is read from the eligibility response's MRI/CAT Scan or X-Ray block, or is the rule's fixed amount. A CPT group with no block (pulmonary function tests) always needs a fixed amount.",
  "Every line in a matching CPT group draws from that one amount, capped at the lines' allowed amounts, instead of its usual copay.",
  "Only at clinic accounts the benefit engine runs, on checks run under the MRI/CAT Scan + Specialist Office + Surgical + X-Ray Service Type (237), on primary insurance. The estimate form shows it as “Add-on copay”.",
  "Only the Extra copay kind is active. Copay only and Co-insurance are coming soon.",
];

export const KIND_LABELS: Record<ClinicCopayRuleKind, string> = {
  EXTRA_COPAY: "Extra copay",
  COPAY_ONLY: "Copay only",
  COINSURANCE: "Co-insurance",
};

/** The kind dropdown: all three, the two not built yet disabled. */
export const KIND_OPTIONS: {
  label: string;
  value: ClinicCopayRuleKind;
  disabled: boolean;
}[] = [
  { label: KIND_LABELS.EXTRA_COPAY, value: "EXTRA_COPAY", disabled: false },
  {
    label: `${KIND_LABELS.COPAY_ONLY} (coming soon)`,
    value: "COPAY_ONLY",
    disabled: true,
  },
  {
    label: `${KIND_LABELS.COINSURANCE} (coming soon)`,
    value: "COINSURANCE",
    disabled: true,
  },
];

const dollars = (cents: number): string =>
  (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

export const amountInWords = (rule: IClinicCopayRule): string =>
  rule.fixedAmountInCents === null
    ? "Read from the eligibility response"
    : `${dollars(rule.fixedAmountInCents)} fixed`;

export interface IClinicCopayDraft {
  payerId: number | null;
  procedureGroup: PracticeTypeRuleModality | null;
  kind: ClinicCopayRuleKind;
  useFixedAmount: boolean;
  amountDollars: number | null;
  enabled: boolean;
}

export const toClinicCopayDraft = (
  rule: IClinicCopayRule | null
): IClinicCopayDraft => ({
  payerId: rule?.payerId ?? null,
  procedureGroup: rule?.procedureGroup ?? "CT",
  kind: rule?.kind ?? "EXTRA_COPAY",
  useFixedAmount: (rule?.fixedAmountInCents ?? null) !== null,
  amountDollars:
    rule?.fixedAmountInCents == null ? null : rule.fixedAmountInCents / 100,
  enabled: rule?.enabled ?? true,
});

const hasBlock = (
  group: PracticeTypeRuleModality | null,
  blockGroups: readonly string[]
): boolean => group !== null && blockGroups.includes(group);

/** The amount field shows for a group with no block, or when the rule takes a fixed amount. */
export const showsAmountField = (
  draft: IClinicCopayDraft,
  blockGroups: readonly string[]
): boolean =>
  draft.useFixedAmount || !hasBlock(draft.procedureGroup, blockGroups);

/** What is wrong with the draft, in words, or null. `editing`: the rule's own key. */
export const clinicCopayDraftProblem = (
  draft: IClinicCopayDraft,
  existing: IClinicCopayRule[],
  blockGroups: readonly string[],
  editing: { payerId: number; procedureGroup: string } | null
): string | null => {
  if (draft.payerId === null) return "Choose the payer.";
  if (draft.procedureGroup === null) return "Choose the CPT group.";
  if (draft.kind !== "EXTRA_COPAY") {
    return `${
      KIND_LABELS[draft.kind]
    } rules are not active yet; only Extra copay can be saved.`;
  }
  if (showsAmountField(draft, blockGroups)) {
    if (
      draft.amountDollars === null ||
      Number.isNaN(draft.amountDollars) ||
      draft.amountDollars < 0
    ) {
      return hasBlock(draft.procedureGroup, blockGroups)
        ? "Type the fixed amount ($0 or more), or untick “Use a fixed amount” to read it from the eligibility response."
        : "This CPT group has no benefit block to read, so it needs a fixed amount ($0 or more).";
    }
  }
  const sameKey = (rule: { payerId: number; procedureGroup: string }) =>
    rule.payerId === draft.payerId &&
    rule.procedureGroup === draft.procedureGroup;
  if ((!editing || !sameKey(editing)) && existing.some(sameKey)) {
    return "This payer already has a rule for this CPT group. Edit that rule instead.";
  }
  return null;
};

/** The body to send: dollars become whole cents; no fixed amount when it is read from the response. */
export const clinicCopayToSaveRequest = (
  draft: IClinicCopayDraft,
  blockGroups: readonly string[]
): IClinicCopayRuleSaveRequest => ({
  payerId: draft.payerId as number,
  procedureGroup: draft.procedureGroup as PracticeTypeRuleModality,
  kind: draft.kind,
  fixedAmountInCents:
    showsAmountField(draft, blockGroups) && draft.amountDollars !== null
      ? Math.round(draft.amountDollars * 100)
      : null,
  enabled: draft.enabled,
});
